import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { QRCodeCanvas } from "qrcode.react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper,
} from "@mui/material";

function FoodWasteIntakeForm() {
  const [formData, setFormData] = useState({
    collectionPoint: "",
    category: "",
    specificFoodItem: "",
    wetWeight: "",
    wetWeightLbs: "",
  });

  const [dropdownData, setDropdownData] = useState({
    collectionPoints: [],
    categories: [],
    foodItems: [],
  });

  const [submittedData, setSubmittedData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const snapshot = await getDocs(collection(db, "shipments"));
        const shipments = snapshot.docs.map((doc) => doc.data());

        const collectionPoints = [...new Set(shipments.map((s) => s["Collection Point"]))];
        const categories = [...new Set(shipments.map((s) => s["Category"]))];
        const foodItems = [...new Set(shipments.map((s) => s["Specific Food Item"]))];

        setDropdownData({ collectionPoints, categories, foodItems });
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      ...(name === "wetWeight" && { wetWeightLbs: (value * 0.00220462).toFixed(2) }),
      ...(name === "wetWeightLbs" && { wetWeight: (value / 0.00220462).toFixed(2) }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    try {
      const intakeDate = new Date();
      const intakeDateFormatted = `${String(intakeDate.getMonth() + 1).padStart(2, "0")}${String(intakeDate.getDate()).padStart(2, "0")}${intakeDate.getFullYear()}`;
      const timestamp = Date.now();
      const idNumber = `${intakeDateFormatted}-${formData.collectionPoint}-${formData.specificFoodItem}-${timestamp}`;
  
      // ✅ Generate the QR code link that directs to shipment details
      const qrCodeUrl = `${window.location.origin}/shipment-details/${idNumber}`;
  
      const newShipment = {
        "ID Number": idNumber,
        "Collection Point": formData.collectionPoint,
        Category: formData.category,
        "Specific Food Item": formData.specificFoodItem,
  
        // ✅ Keep existing MM/DD/YYYY format for reference
        "Intake Date (MM/DD/YYYY)": `${String(intakeDate.getMonth() + 1).padStart(2, "0")}/${String(intakeDate.getDate()).padStart(2, "0")}/${intakeDate.getFullYear()}`,
  
        // ✅ Add YYYY-MM-DD format for better sorting/filtering
        "Intake Date (YYYY-MM-DD)": `${intakeDate.getFullYear()}-${String(intakeDate.getMonth() + 1).padStart(2, "0")}-${String(intakeDate.getDate()).padStart(2, "0")}`,
  
        "Wet Weight (g)": formData.wetWeight,
        "Wet Weight (lbs)": formData.wetWeightLbs,
  
        // ✅ Store the QR code value and URL for linking
        qrCodeValue: idNumber,
        qrCodeUrl: qrCodeUrl,
      };
  
      await addDoc(collection(db, "shipments"), newShipment);
  
      setSubmittedData(newShipment);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  

  const handlePrint = () => {
    window.print();
  };

  if (submittedData) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h5" sx={{ color: "#d9d9d9", mb: 2 }}>
          QR Code & ID Number
        </Typography>
        <Paper sx={{ p: 4, display: "inline-block", textAlign: "center" }}>
          <QRCodeCanvas value={submittedData["qrCodeValue"]} size={150} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {submittedData["ID Number"]}
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={handlePrint}>
            Print
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
      <Typography variant="h7" sx={{ color: "#d9d9d9", textAlign: "center" }}>
        Food Waste Intake Form
      </Typography>
      <Typography component="p" sx={{ color: "#d9d9d9", textAlign: "center", cursor: "pointer" }} onClick={() => navigate("/shipments/new")}>
        ← Back to Form Selection
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField select fullWidth label="Collection Point" name="collectionPoint" value={formData.collectionPoint} onChange={handleChange} sx={{ mb: 2 }} required>
            {dropdownData.collectionPoints.map((point, index) => (
              <MenuItem key={index} value={point}>{point}</MenuItem>
            ))}
          </TextField>

          <TextField select fullWidth label="Category" name="category" value={formData.category} onChange={handleChange} sx={{ mb: 2 }} required>
            {dropdownData.categories.map((category, index) => (
              <MenuItem key={index} value={category}>{category}</MenuItem>
            ))}
          </TextField>

          <TextField select fullWidth label="Specific Food Item" name="specificFoodItem" value={formData.specificFoodItem} onChange={handleChange} sx={{ mb: 2 }} required>
            {dropdownData.foodItems.map((item, index) => (
              <MenuItem key={index} value={item}>{item}</MenuItem>
            ))}
          </TextField>

          <TextField fullWidth label="Wet Weight (g)" name="wetWeight" type="number" value={formData.wetWeight} onChange={handleChange} sx={{ mb: 2 }} required />
          <TextField fullWidth label="Wet Weight (lbs)" name="wetWeightLbs" type="number" value={formData.wetWeightLbs} onChange={handleChange} sx={{ mb: 2 }} required />

          <Button type="submit" variant="contained" fullWidth>Submit</Button>
        </form>
      </Paper>
    </Box>
  );
}

export default FoodWasteIntakeForm;
